import {
  AppPageHeader,
  ColumnsType,
  InfiniteTable,
} from '@/components/_common';
import { Button, Col, Modal, ModalProps, Row } from 'antd';
import ModalConsumableSelect from '../ModalConsumableSelect';
import {
  ListWorkOrdersQuery,
  ListWorkOrdersResult,
} from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { useDebounce, useDisclosure } from '@/hooks';
import { useState } from 'react';
import { CheckOutlined } from '@ant-design/icons';
import { WorkOrderComplementStatusNumber } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { useWorkOrders } from '@/core/domain/workorder/WorkOrders/WorkOrders.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import ModalWorkOrdersExtraFilters from './ModalWorkOrdersExtraFilters';
import { ListStockPartsDeliveryDataSupport } from '@/core/domain/stock/DeliveryMaterial/DeliveryMaterial.types';

interface Props extends ModalProps {
  onCancel: () => void;
  companyId: number;
  consumablesSelected?: ListStockPartsDeliveryDataSupport[];
  handlerConsumablesSelect: (part: ListStockPartsDeliveryDataSupport[]) => void;
}

const ModalWorkOrderSelect = ({
  companyId,
  consumablesSelected,
  handlerConsumablesSelect,
  ...props
}: Props) => {
  const intl = useIntl().formatMessage;

  const [selectConsumableVisible, setSelectConsumableVisible] =
    useDisclosure(false);
  const [workOrderSelect, setWorkOrderSelect] = useState<
    ListWorkOrdersResult | undefined
  >();

  const [queryParams, setQueryParams] = useState<ListWorkOrdersQuery>({
    order: 1,
    status: [WorkOrderComplementStatusNumber.Authorized],
  });

  const queryDebounce = useDebounce(queryParams, 500);

  const {
    data: workOrders,
    isFetching,
    hasNextPage,
    fetchNextPage,
  } = useWorkOrders(
    { ...queryDebounce, companiesIds: [companyId] },
    props.open,
  );

  const data = flatInfiniteData(workOrders);

  const columns: ColumnsType<ListWorkOrdersResult> = [
    {
      dataIndex: 'complementIdentifier',
      title: intl({
        id: 'deliveryOrReturnOfMaterials.deliverMaterial.modalDelivery.os',
      }),
      ellipsis: true,
    },
    {
      dataIndex: 'licensePlate',
      title: intl({
        id: 'deliveryOrReturnOfMaterials.deliverMaterial.modalDelivery.board',
      }),
      ellipsis: true,
    },
    {
      dataIndex: ['customer', 'name'],
      title: intl({
        id: 'deliveryOrReturnOfMaterials.deliverMaterial.modalDelivery.estimator',
      }),
      ellipsis: true,
    },
    {
      dataIndex: 'authorizationDate',
      title: intl({
        id: 'deliveryOrReturnOfMaterials.deliverMaterial.modalDelivery.authorizationDate',
      }),
      ellipsis: true,
      render: (value: string) => {
        if (!value) {
          return '-';
        }
        return moment(value).format('DD/MM/YYYY');
      },
    },
    {
      key: 'action',
      dataIndex: 'action',
      align: 'right',
      render: (_, item) => (
        <Button
          type="primary"
          onClick={() => {
            setWorkOrderSelect(item);
            setSelectConsumableVisible.toggle();
          }}
          icon={<CheckOutlined />}
        />
      ),
    },
  ];

  return (
    <Modal {...props} destroyOnClose title="Selecionar insumos" width={'95%'}>
      <>
        <AppPageHeader
          onSearch={(value) =>
            setQueryParams((prev) => ({ ...prev, search: value }))
          }
          searchValue={queryParams.search}
          headerActions={({ isShowingFilters }) =>
            isShowingFilters && (
              <Button
                type="primary"
                onClick={() =>
                  setQueryParams({
                    order: 1,
                    status: [WorkOrderComplementStatusNumber.Authorized],
                    companiesIds: [companyId],
                  })
                }
              >
                {intl({ id: 'commons.clear.filters' })}
              </Button>
            )
          }
          extraFilters={
            <ModalWorkOrdersExtraFilters
              queryParams={queryParams}
              setQueryParams={setQueryParams}
            />
          }
        />

        <Row gutter={[24, 24]}>
          <Col span={24}>
            <InfiniteTable<ListWorkOrdersResult>
              loading={isFetching}
              hasNextPage={hasNextPage}
              fetchNextPage={fetchNextPage}
              pagination={false}
              dataSource={data}
              columns={columns}
            />
          </Col>

          {workOrderSelect && (
            <ModalConsumableSelect
              workOrderSelect={workOrderSelect}
              consumablesSelected={consumablesSelected}
              handlerConsumablesSelect={handlerConsumablesSelect}
              open={selectConsumableVisible}
              onCancel={() => {
                props.onCancel(), setSelectConsumableVisible.toggle();
              }}
            />
          )}
        </Row>
      </>
    </Modal>
  );
};

export default ModalWorkOrderSelect;
